import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import { Card, Typography } from '@one-thd/sui-atomic-components';
import { OrderTracking } from '@one-thd/sui-icons';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { FulfillmentsCarousel } from './fulfillments-carousel/FulfillmentsCarousel';
import { FulfillmentDetailsDrawer } from './fulfillment-details/FulfillmentDetailsDrawer';
import { ActiveOrdersLoading } from './ActiveOrdersLoading';
import { ActiveOrdersError } from './ActiveOrdersError';
import { ActiveOrdersNullState } from './ActiveOrdersNullState';
// OOP-4207: restore with the "View All" link in renderHeader
// import { ORDER_DETAILS_URL } from '../constants';
import { CommonProvider, useCommonContext } from '../contexts/common-context';
import { resetRetryTracking } from '../utils/retryTracker';
import { IActiveDistribution } from '../models/active-orders';

export interface IActiveOrdersData {
  customerAccountId?: string;
  userId?: string;
  totalCardCount?: number;
  totalDeliveryCount?: number;
  totalPickupCount?: number;
  activeDistributions?: IActiveDistribution[];
}

export interface IActiveOrdersViewProps {
  data: IActiveOrdersData;
  customerType?: string;
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
  onCardClick?: (distribution?: IActiveDistribution) => void;
  componentLocation?: string;
  isDashboardSnapshot?: boolean;
}

const ActiveOrdersContent = ({
  data,
  customerType = '',
  isLoading = false,
  isError = false,
  onRetry = undefined,
  onCardClick = undefined,
  componentLocation = 'default',
  isDashboardSnapshot = false,
}: IActiveOrdersViewProps) => {
  const activeDistributions = data?.activeDistributions || [];
  const totalCardCount = data?.totalCardCount ?? activeDistributions.length;
  const [isDetailsDrawerOpen, setIsDetailsDrawerOpen] = useState(false);
  const [activeFulfillmentIndex, setActiveFulfillmentIndex] = useState<
    number | null
  >(null);

  const { isMobile } = useCommonContext();

  // Reset retry tracking on successful load
  useEffect(() => {
    if (!isLoading && !isError) {
      resetRetryTracking();
    }
  }, [isLoading, isError]);

  const handleCardClick = (index: number) => {
    const distribution = activeDistributions?.[index];
    setActiveFulfillmentIndex(index);
    setIsDetailsDrawerOpen(true);
    if (onCardClick) {
      onCardClick(distribution);
    }
  };

  const renderHeader = (cardCount: number) => (
    <div className="sui-flex sui-items-start sui-justify-between sui-gap-4 sui-w-full">
      <div className="sui-flex sui-items-center sui-gap-3 sui-min-w-0">
        <span className="sui-flex sui-shrink-0 sui-text-primary">
          <OrderTracking />
        </span>
        <div className="sui-flex sui-flex-wrap sui-items-center sui-gap-x-2 sui-gap-y-1">
          <Typography
            variant={isMobile ? 'h5' : 'h4'}
            weight="display"
            className="sui-leading-none"
          >
            Active Deliveries & Pickups
          </Typography>
          {cardCount > 0 && (
            <Typography variant="body-base" className="sui-leading-none sui-whitespace-nowrap">
              ({cardCount})
            </Typography>
          )}
        </div>
      </div>
      {/*
        OOP-4207: the "View All" link is disabled until the order details page at
        ORDER_DETAILS_URL is ready. To restore it, uncomment this block, add `Link`
        to the @one-thd/sui-atomic-components import, and uncomment the
        `ORDER_DETAILS_URL` import above. Keep comments out of import braces: the
        experience build's es-module-lexer fails on them (OOP-4223).

        <Link href={ORDER_DETAILS_URL} className="sui-hidden sm:sui-inline sui-whitespace-nowrap">
          View All
        </Link>
      */}
    </div>
  );

  const renderContainer = (content: React.ReactNode, cardCount = totalCardCount) => {
    return (
      <div
        data-component="ActiveOrders"
        data-testid="active-orders"
        data-analytics-module="active-orders"
        data-component-location={componentLocation || 'default'}
      >
        <ErrorBoundary name="active-orders">
          <Card
            className={classNames('sui-h-full', {
              'sui-border-1 sui-border-primary sui-rounded-base': !isDashboardSnapshot,
            })}
            disablePadding
            // Card defaults to variant="border" (sui-paper-outlined), which the class toggle above
            // doesn't reach. A Dashboard Snapshot host already supplies the bordered panel, so drop
            // Paper's own chrome too — same as Projects/Lists/Quotes in that host.
            PaperProps={isDashboardSnapshot ? { variant: 'shadow', shadow: 'none' } : undefined}
            CardContentProps={{
              className: classNames(
                'sui-flex',
                'sui-flex-col',
                'sui-gap-4',
                'sui-h-full',
                'sui-w-full',
                { 'sui-p-4': !isDashboardSnapshot }
              )
            }}
          >
            {!isDashboardSnapshot && renderHeader(cardCount)}
            {content}
          </Card>
        </ErrorBoundary>
      </div>
    );
  };

  if (isLoading) {
    return renderContainer(<ActiveOrdersLoading cardCount={5} />, 0);
  }

  if (isError) {
    return (
      <ErrorBoundary name="active-orders-error">
        <ActiveOrdersError
          refresh={onRetry || (() => {})}
          componentLocation={componentLocation}
          isDashboardSnapshot={isDashboardSnapshot}
        />
      </ErrorBoundary>
    );
  }

  if (activeDistributions?.length > 0) {
    return (
      <>
        {renderContainer(
          <FulfillmentsCarousel
            activeDistributions={activeDistributions}
            customerType={customerType || ''}
            onCardClick={(index) => handleCardClick(index)}
            componentLocation={componentLocation}
          />
        )}
        <ErrorBoundary name="active-orders-drawer">
          <FulfillmentDetailsDrawer
            open={isDetailsDrawerOpen}
            distribution={
              activeDistributions?.[activeFulfillmentIndex ?? 0]
            }
            onClose={() => setIsDetailsDrawerOpen(false)}
            customerType={customerType || ''}
            isMobile={isMobile}
          />
        </ErrorBoundary>
      </>
    );
  }

  return (
    <ActiveOrdersNullState
      componentLocation={componentLocation}
      isDashboardSnapshot={isDashboardSnapshot}
    />
  );
};

/**
 * The presentational half of Active Orders. It renders whatever it is given and
 * fetches nothing.
 *
 * Internal to this package. `ActiveOrders` is the exported component, and it
 * supplies these props from `useActiveOrders`.
 */
const ActiveOrdersView = ({
  data,
  customerType = '',
  isLoading = false,
  isError = false,
  onRetry = undefined,
  onCardClick = undefined,
  componentLocation = 'default',
  isDashboardSnapshot = false,
}: IActiveOrdersViewProps) => {
  return (
    <CommonProvider>
      <ActiveOrdersContent
        data={data}
        customerType={customerType}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        onCardClick={onCardClick}
        componentLocation={componentLocation}
        isDashboardSnapshot={isDashboardSnapshot}
      />
    </CommonProvider>
  );
};

ActiveOrdersContent.defaultProps = {
  customerType: '',
  isLoading: false,
  isError: false,
  onRetry: undefined,
  onCardClick: undefined,
  componentLocation: 'default',
  isDashboardSnapshot: false,
};

ActiveOrdersView.defaultProps = {
  customerType: '',
  isLoading: false,
  isError: false,
  onRetry: undefined,
  onCardClick: undefined,
  componentLocation: 'default',
  isDashboardSnapshot: false,
};

export { ActiveOrdersView };
